import { Box, Paper } from '@material-ui/core'
import { useContext, useMemo, useState } from 'react'
import { RootContext } from '../../..'
import { TransportTask } from '../../../api/smartbestbuys-api'
import { FilteredTable, Link, Topic, withLayout } from '../../../custom-components'
import { defaultEndDate, defaultStartDate } from '../../../data'
import { NavigationItemKey, PageName, TaskStatus, TaskType } from '../../../enums'
import { dateFormat, dateTimeFormat, getV3Url } from '../../../functions'
import { formatNumberInt } from '../../../functions/number-utils'
import { useTransportTasks } from '../../../hooks/transport'
import {
  MessengerIcon,
  NotificationHead,
  PrepareDocumentIcon,
  PrepareProductIcon,
  ReturnDocumentIcon,
  ReturnProductIcon,
  Space,
  TaskStatusChip,
} from '../../../shared-components'
import './TransportTaskListPage.less'

const TransportTaskListPage = () => {
  // root context
  const { setCurrentPage } = useContext(RootContext)
  setCurrentPage(PageName.TRANSPORT)

  // state
  const [startDate, setStartDate] = useState(defaultStartDate)
  const [endDate, setEndDate] = useState(defaultEndDate)
  const [tabValue, setTabValue] = useState<TaskStatus>(TaskStatus.TransportPending)

  const transportTasks = useTransportTasks(startDate, endDate, TaskType.TransportScheduleTask)
  const pendingAmount = useMemo(
    () => transportTasks.filter((t) => t.status.id === TaskStatus.TransportPending).length,
    // eslint-disable-next-line
    [transportTasks.map((t) => t.status.id).join(',')],
  )

  const pendingApprovalAmount = useMemo(
    () => transportTasks.filter((t) => t.status.id === TaskStatus.PendingApproval).length,
    // eslint-disable-next-line
    [transportTasks.map((t) => t.status.id).join(',')],
  )

  return (
    <Box p={2}>
      <Topic>รายการบริการงานขนส่ง</Topic>
      <NotificationHead navigationKey={NavigationItemKey.TransportTask} />
      <Space />
      <Box p={2} clone>
        <Paper>
          <FilteredTable<TransportTask>
            color="primary.dark"
            columns={[
              {
                name: 'สถานะงานจัดส่ง',
                render: ({ status }) => <TaskStatusChip size="small" status={status.id} label={status.name} />,
              },
              {
                name: 'เลขที่งานจัดส่ง',
                render: ({ id, code }) =>
                  code.startsWith('TSx') ? (
                    <Link color="primary" to={`/transport-tasks/${id}/profile`}>
                      {code}
                    </Link>
                  ) : (
                    <Link color="textSecondary" target="_blank" href={getV3Url('/transport-tasks')}>
                      {code}
                    </Link>
                  ),
              },
              {
                name: 'เลขที่ใบสั่งขายอ้างอิง',
                render: ({ saleOrderInfo }) =>
                  saleOrderInfo ? (
                    <Link color="secondary" to={`/sale-orders/${saleOrderInfo.id}/profile`}>
                      {saleOrderInfo.code}
                    </Link>
                  ) : (
                    '-'
                  ),
              },
              {
                name: 'วันที่ต้องจัดส่ง',
                dataIndex: 'dueDate',
                render: ({ dueDate }) => (dueDate ? dateFormat(dueDate) : 'ยังไม่ระบุ'),
              },
              { name: 'ชื่อลูกค้า', render: ({ saleOrderInfo }) => saleOrderInfo?.customerName ?? '-' },
              {
                name: 'สถานะงานเตรียมสินค้า',
                render: ({ relatedTasks }) => {
                  const prepareProduct = relatedTasks?.find((t) => t.type.id === TaskType.WarehousePrepareProduct)
                  const prepareDocument = relatedTasks?.find((t) => t.type.id === TaskType.FinancePrepareDocument)
                  const shippingTask = relatedTasks?.find((t) => t.type.id === TaskType.MessengerShippingTask)
                  const returnProduct = relatedTasks?.find((t) => t.type.id === TaskType.WarehouseReturnProduct)
                  const returnDocument = relatedTasks?.find((t) => t.type.id === TaskType.FinanceReturnDocument)
                  return (
                    <div className="transport-task-icon-group">
                      <PrepareProductIcon status={prepareProduct?.status.id} />
                      <PrepareDocumentIcon status={prepareDocument?.status.id} />
                      <MessengerIcon status={shippingTask?.status.id} />
                      <ReturnProductIcon status={returnProduct?.status.id} />
                      <ReturnDocumentIcon status={returnDocument?.status.id} />
                    </div>
                  )
                },
              },
              {
                name: 'วันที่สร้าง',
                dataIndex: 'recCreatedAt',
                render: ({ recCreatedAt }) => dateTimeFormat(recCreatedAt),
              },
            ]}
            data={transportTasks.filter((t) => t.status.id === tabValue)}
            defaultDateRage={{
              startDate: defaultStartDate,
              endDate: defaultEndDate,
            }}
            onDateChange={(startDate, endDate) => {
              setStartDate(startDate)
              setEndDate(endDate)
            }}
            cleanSpace
            tabOption={{
              tabs: [
                {
                  label: `รอยืนยันข้อมูลจัดส่ง (${formatNumberInt(pendingAmount)})`,
                  value: TaskStatus.TransportPending,
                },
                { label: 'กำลังดำเนินการ', value: TaskStatus.Processing },
                { label: `รอจบงาน (${formatNumberInt(pendingApprovalAmount)})`, value: TaskStatus.PendingApproval },
                { label: 'จบงานแล้ว', value: TaskStatus.Done },
                { label: 'งานที่ยกเลิก', value: TaskStatus.Canceled },
              ],
              value: tabValue,
              onChange: (_, newValue) => setTabValue(newValue),
            }}
          />
        </Paper>
      </Box>
    </Box>
  )
}

export default withLayout(TransportTaskListPage)
